import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  X,
  SlidersHorizontal,
  Clock,
  FileText,
  Calendar,
  AlertCircle,
  Building,
  BookOpen
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import AdBanner from '../components/AdBanner';
import '../styles/editorial-list.css';

const Research = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('research'); // 'research' or 'thesis'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedInstitution, setSelectedInstitution] = useState('all');
  const [availableCategories, setAvailableCategories] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [lastRead, setLastRead] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 6;

  const years = ['all', '2026', '2025', '2024', '2023', '2022', '2021', '2020'];
  const institutions = [
    'all',
    'AIIMS Delhi',
    'BHU Varanasi', 
    'Gujarat Ayurved University',
    'IPGT&RA Jamnagar',
    'National Institute of Ayurveda',
    'Quadra Institute of Ayurveda',
    'SDM College Udupi'
  ];

  // Fetch live research paper / thesis feed from backend API
  const fetchFeed = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        category: selectedCategory === 'all' ? null : selectedCategory,
        year: selectedYear === 'all' ? null : parseInt(selectedYear),
        institution: selectedInstitution === 'all' ? null : selectedInstitution,
        search: searchTerm.trim() || null,
        page: currentPage,
        limit: itemsPerPage
      };

      let responseData;
      if (activeTab === 'research') {
        responseData = await apiService.getResearchFeed(params);
      } else {
        responseData = await apiService.getThesisFeed(params);
      }

      const list = responseData?.items || responseData || [];
      const total = responseData?.totalCount || list.length;
      
      setItems(list);
      setTotalCount(total);

      // Dynamically accumulate category pills
      const catSet = new Set(list.map(item => item.category).filter(Boolean));
      setAvailableCategories(['all', ...Array.from(catSet)]);

    } catch (err) {
      console.error(`Error loading live ${activeTab} feed:`, err);
      setError(err.message || `Failed to load ${activeTab} repository from server.`);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedCategory, selectedYear, selectedInstitution, searchTerm, currentPage]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Load last read session from localStorage
  useEffect(() => {
    if (isAuthenticated()) {
      const stored = localStorage.getItem('last_read_research');
      if (stored) {
        try {
          setLastRead(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing last read research:', e);
        }
      }
    } else {
      setLastRead(null);
    }
  }, [isAuthenticated]);

  const openReader = (item) => {
    const type = activeTab === 'research' ? 'research' : 'thesis';
    const authorStr = type === 'research'
      ? (Array.isArray(item.authors) ? item.authors.join(', ') : item.authors)
      : item.author;

    localStorage.setItem('last_read_research', JSON.stringify({
      id: item.id,
      title: item.title,
      author: authorStr,
      slug: item.slug,
      type: type
    }));
    
    navigate(`/${type}/${item.slug}`);
  };

  // Helper for category badge styling
  const getCatClass = (category) => {
    if (!category) return 'cat-default';
    const c = category.toLowerCase();
    if (c.includes('clinical') || c.includes('phd')) return 'cat-ayurveda';
    if (c.includes('research') || c.includes('ms')) return 'cat-shalya';
    if (c.includes('case') || c.includes('md')) return 'cat-case';
    return 'cat-default';
  };

  // Sort local page items
  const sortedItems = [...items].sort((a, b) => {
    if (sortBy === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    } else if (sortBy === 'views') {
      return (b.downloadCount || b.views || 0) - (a.downloadCount || a.views || 0);
    } else if (sortBy === 'date') {
      const dateA = new Date(a.publishedDate || a.year || 0);
      const dateB = new Date(b.publishedDate || b.year || 0);
      return dateB - dateA;
    }
    return 0;
  });

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (num) => {
    setCurrentPage(num);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedYear('all');
    setSelectedInstitution('all');
    setCurrentPage(1);
  };

  return (
    <div className="editorial-page-container">
      
      {/* Top Menu-Adjacent Search, Filters & Tab Control Bar */}
      <div className="top-menu-control-bar">
        <div className="container">
          <div className="top-control-bar-inner">
            
            {/* Repository Tab Switcher */}
            <div className="control-pills-group" style={{ marginRight: '8px' }}>
              <button 
                className={`control-pill ${activeTab === 'research' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('research');
                  resetFilters();
                }}
              >
                <FileText size={14} style={{ marginRight: '4px' }} />
                Research Papers
              </button>
              <button 
                className={`control-pill ${activeTab === 'thesis' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('thesis');
                  resetFilters();
                }}
              >
                <BookOpen size={14} style={{ marginRight: '4px' }} />
                Thesis Archive
              </button>
            </div>

            {/* Search Input */}
            <div className="control-search-box">
              <Search className="control-search-icon" size={17} />
              <input
                type="text"
                placeholder={`Search ${activeTab === 'research' ? 'research papers' : 'theses'} by title, author, keyword...`}
                className="control-search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              {searchTerm && (
                <button
                  className="control-search-clear"
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  title="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Category Pills */}
            <div className="control-pills-group">
              {availableCategories.map((cat) => (
                <button
                  key={cat}
                  className={`control-pill ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                  }}
                >
                  {cat === 'all' ? 'All Categories' : cat.replace('-', ' ')}
                </button>
              ))}
            </div>

            {/* Year & Institution Filters */}
            <div className="control-sort-box" style={{ gap: '8px' }}>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setCurrentPage(1);
                }}
                className="control-sort-select"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y === 'all' ? 'All Years' : y}</option>
                ))}
              </select>

              <select
                value={selectedInstitution}
                onChange={(e) => {
                  setSelectedInstitution(e.target.value);
                  setCurrentPage(1);
                }}
                className="control-sort-select"
              >
                {institutions.map(inst => (
                  <option key={inst} value={inst}>{inst === 'all' ? 'All Institutions' : inst}</option>
                ))}
              </select>

              <SlidersHorizontal size={15} color="#94a3b8" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="control-sort-select"
              >
                <option value="date">Sort: Latest</option>
                <option value="views">Sort: Downloads</option>
                <option value="title">Sort: Title A-Z</option>
              </select>
            </div>

          </div>
        </div>
      </div>

      {/* Main 2-Column Editorial Container */}
      <div className="container">
        
        {/* Resume Reading Quick Widget (if available) */}
        {isAuthenticated() && lastRead && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
            border: '1px solid rgba(0, 229, 255, 0.25)',
            borderRadius: '14px',
            padding: '12px 20px',
            marginBottom: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0,229,255,0.2)', color: '#00e5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} />
              </div>
              <div>
                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#00e5ff', fontWeight: 600, letterSpacing: '0.5px' }}>
                  Resume {lastRead.type === 'research' ? 'Research Paper' : 'Thesis'}
                </span>
                <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#ffffff' }}>{lastRead.title}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button 
                onClick={() => navigate(`/${lastRead.type}/${lastRead.slug}`)}
                className="editorial-read-btn"
                style={{ padding: '6px 14px' }}
              >
                Resume <ChevronRight size={14} />
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('last_read_research');
                  setLastRead(null);
                }}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                title="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <div className="editorial-layout-grid">
          
          {/* Left Main Column: Editorial Publication Feed List (~70%) */}
          <main className="editorial-feed-column">
            
            {/* Loading Indicator */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="loader" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: '#94a3b8' }}>Fetching {activeTab === 'research' ? 'research papers' : 'theses'} from repository...</p>
              </div>
            )}

            {/* Error Banner */}
            {!loading && error && (
              <div style={{ textAlign: 'center', padding: '40px 20px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '16px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <AlertCircle size={36} color="#ef4444" style={{ marginBottom: '12px' }} />
                <h3 style={{ color: '#f8fafc', marginBottom: '6px' }}>Unable to load repository</h3>
                <p style={{ color: '#fca5a5', fontSize: '0.9rem' }}>{error}</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && sortedItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <FileText size={48} color="#64748b" style={{ marginBottom: '16px' }} />
                <h3 style={{ color: '#f8fafc', marginBottom: '8px' }}>No publications found</h3>
                <p style={{ color: '#94a3b8' }}>No entries match your current search or category filter.</p>
                <button className="editorial-read-btn" onClick={resetFilters} style={{ marginTop: '16px' }}>
                  Reset Filters
                </button>
              </div>
            ) : !loading && !error && (
              sortedItems.map((item) => (
                <article 
                  key={item.id} 
                  className="editorial-list-card"
                  onClick={() => openReader(item)}
                  style={{ cursor: 'pointer' }}
                >
                    
                    {/* Left Cover Image Thumbnail / Fallback Badge */}
                    <div className="editorial-thumb-wrapper">
                      {item.coverImageUrl ? (
                        <img src={item.coverImageUrl} alt={item.title} className="editorial-thumb-img" />
                      ) : (
                        <div className="editorial-thumb-fallback">
                          <FileText size={28} />
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.9 }}>
                            {item.category || (activeTab === 'research' ? 'Paper' : 'Thesis')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Right Content & Meta Details */}
                    <div className="editorial-card-content">
                      <div>
                        {/* Category Chip & Time/Metadata */}
                        <div className="editorial-card-meta-top">
                          <span className={`editorial-cat-chip ${getCatClass(item.category)}`}>
                            {item.category || (activeTab === 'research' ? 'Research Paper' : 'Thesis')}
                          </span>
                          <span className="editorial-meta-item">
                            <Calendar size={13} /> {item.year}
                          </span>
                          {item.institution && (
                            <span className="editorial-meta-item">
                              <Building size={13} /> {item.institution}
                            </span>
                          )}
                          {item.pages > 0 && (
                            <span className="editorial-meta-item">
                              <Clock size={13} /> {item.pages} pgs
                            </span>
                          )}
                        </div>

                        {/* Title & Excerpt */}
                        <h2 className="editorial-card-title">
                          {item.title}
                        </h2>
                        <p className="editorial-card-excerpt">
                          {item.abstract || 'Click to read full publication.'}
                        </p>
                      </div>
                    </div>

                  </article>
                ))
            )}

            {/* Pagination Controls */}
            {!loading && !error && totalPages > 1 && (
              <div className="editorial-pagination-bar">
                <button
                  className="pagination-btn"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} /> Previous
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`page-num-dot ${currentPage === i + 1 ? 'active' : ''}`}
                      onClick={() => goToPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  className="pagination-btn"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}

          </main>

          {/* Right Sticky Sidebar Pane: Google AdSense (~30%) */}
          <aside className="editorial-sidebar-pane">
            
            {/* Google AdSense Display Banner Widget 1 */}
            <div className="adsense-sidebar-widget">
              <span className="adsense-label">Advertisement</span>
              <AdBanner type="square" />
            </div>

            {/* Google AdSense Skyscraper Banner Widget 2 */}
            <div className="adsense-sidebar-widget">
              <span className="adsense-label">Sponsor</span>
              <AdBanner type="skyscraper" />
            </div>

          </aside>

        </div>
      </div>
    </div>
  );
};

export default Research;
